import { Helmet } from 'react-helmet';
import React, { useState, useEffect, useCallback } from 'react';
import { getUserProjects, createProject, updateProject, deleteProject, getUserFiles, delFilesFromProject, getSharedProjects } from './../api';
import { Table, Modal, Button, Form, Pagination, Badge, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import UploadFilesTreading from "../components/upload-files-threading.component";
import AddProjectFiles from "../components/AddProjectFiles";
import './Projects.css'
import useAuth from '../hooks/useAuth';

function Projects() {
  const title = 'Projects';
  const auth = useAuth();
  const user = auth.getSession();
  const [isEnterpriseUser, setIsEnterpriseUser] = useState(false);
  const [projects, setProjects] = useState([]);
  const [sharedProjects, setSharedProjects] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showDelFileModal, setShowDelFileModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileToUpload, setFileToUpload] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [selectedExistingFile, setSelectedExistingFile] = useState('');
  const [selectedDelFiles, setSelectedDelFiles] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showHints, setShowHints] = useState(() => {
    // Initialize state from localStorage, default to false if not set
    const saved = localStorage.getItem('showHints');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [listsPerPage] = useState(10);
  const [projectFilter, setProjectFilter] = useState('all'); // 'all', 'owned', or 'shared'

  useEffect(() => {
    fetchUserProjects();
    fetchSharedProjects();
    fetchAllFiles();
    setIsEnterpriseUser(user.is_enterprise);
  }, []);

  const fetchUserProjects = async () => {
    try {
      const response = await getUserProjects();
      //console.log("projects:", response.data);
      // Add isOwner flag to user's own projects
      const ownedProjects = response.data.map(project => ({
        ...project,
        isOwner: true
      }));
      setProjects(ownedProjects);
    } catch (error) {
      console.error('Error fetching user projects:', error);
    }
  };

  const fetchSharedProjects = async () => {
    try {
      // Assuming you have an API endpoint for shared projects
      const response = await getSharedProjects();
      //console.log("shared projects:", response.data);
      // Add isShared flag to shared projects
      const sharedProjectsData = response.data.map(project => ({
        ...project,
        isShared: true
      }));
      setSharedProjects(sharedProjectsData);
    } catch (error) {
      console.error('Error fetching shared projects:', error);
    }
  };

  const fetchAllFiles = async () => {
    try {
      const response = await getUserFiles();
      setAllFiles(response.data);
    } catch (error) {
      console.error('Error fetching all files:', error);
    }
  };

  // Get combined projects for unified list view
  const getAllProjects = () => {
    switch (projectFilter) {
      case 'owned':
        return projects;
      case 'shared':
        return sharedProjects;
      case 'all':
      default:
        return [...projects, ...sharedProjects];
    }
  };


  const handleFileSubmitCompete = () => {
    fetchUserProjects();
    handleFileClose();
  }

  const handleDelFileSubmit = async (event) => {
    event.preventDefault();
    if (!currentProject) return;

    try {
      if (selectedDelFiles.length > 0) {
        //console.log("selectedDelFiles=", selectedDelFiles);
        await delFilesFromProject(currentProject.id, selectedDelFiles);
        fetchUserProjects();
        handleDelFileClose();
      } else {
        console.error('No files selected for deletion');
      }
    } catch (error) {
      console.error('Error handling file:', error);
    }
  };

  const handleUploadComplete = useCallback(() => {
    setIsUploading(false);
    fetchUserProjects();
    handleUploadClose()
  }, []);

  const handleUploadingStatus = (status) => {
    setIsUploading(status);
  };
  const handleFileClose = () => {
    setShowFileModal(false);
    setCurrentProject(null);
    setFileToUpload(null);
    setSelectedExistingFile('');
  };
  const handleDelFileClose = () => {
    setShowDelFileModal(false);
    setCurrentProject(null);
    setSelectedDelFiles([]);
  };

  const handleUploadClose = () => {
    console.log("IsUploading:", isUploading);
    if (isUploading) {
      if (window.confirm("Vector is in progress. The Vector will continue in the background, but you won't see its progress. Are you sure you want to close this window?")) {
        setShowUploadModal(false);
      }
    } else {
      setShowUploadModal(false);
    }
    setCurrentProject(null);
    setFileToUpload(null);
    setSelectedExistingFile('');
  };
  // Remove the handleFileUpload function as it's now consolidated into handleFileSubmit
  const handleProjectClose = () => {
    setShowProjectModal(false);
    setCurrentProject(null);
  };

  const handleProjectShow = (project = null) => {
    setCurrentProject(project);
    setShowProjectModal(true);
  };


  const handleFileShow = (project) => {
    setCurrentProject(project);
    setShowFileModal(true);
  };
  const handleDelFileShow = (project) => {
    setCurrentProject(project);
    setSelectedDelFiles([]);
    setShowDelFileModal(true);
  };
  const handleUploadShow = (project) => {
    setCurrentProject(project);
    setShowUploadModal(true);
  };

  const handleProjectSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const projectData = Object.fromEntries(formData.entries());

    try {
      if (currentProject) {
        await updateProject(currentProject.id, projectData);
      } else {
        await createProject(projectData);
      }
      fetchUserProjects();
      handleProjectClose();
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const filteredProjects = getAllProjects().filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteShow = (project) => {
    setCurrentProject(project);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setCurrentProject(null);
  };

  const handleDeleteProject = async () => {
    try {
      await deleteProject(currentProject.id);
      fetchUserProjects();
      handleDeleteClose();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  useEffect(() => {
    // Update localStorage whenever showHints changes
    localStorage.setItem('showHints', JSON.stringify(showHints));
  }, [showHints]);

  const toggleHints = () => {
    setShowHints(prevState => !prevState);
  };

  // Add a function to handle showing the details modal
  const handleDetailsShow = (project) => {
    setCurrentProject(project);
    setShowDetailsModal(true);
  };

  // Add a function to handle closing the details modal
  const handleDetailsClose = () => {
    setShowDetailsModal(false);
    setCurrentProject(null);
  };

  const indexOfLast = currentPage * listsPerPage;
  const indexOfFirst = indexOfLast - listsPerPage;
  const currentLists = filteredProjects.slice(indexOfFirst, indexOfLast);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredProjects.length / listsPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="container-fluid mb-5 px-3">
        <div className="row g-5 pt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header pt-3 bg-white">

                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="text-secondary">Projects Directory</h4>
                  </div>
                  <div>
                    <div className="form-check form-switch">
                      <div className="form-check form-switch ps-0">
                        <input className="form-check-input" type="checkbox" id="toggleHint" onChange={toggleHints} checked={showHints} />
                        <label className="form-check-label" id="hintLabel" for="toggleHint" checked="">{showHints ? "Hide Hints" : "Show Hints"}</label>
                      </div>
                    </div>

                  </div>
                </div>

                {showHints && (
                  <>
                    <div className="col-12 helpful-hint mt-2">
                      <div class="card p-3 bg-light">
                        <p className="text-mute mb-0"><strong>Helpful Hint:</strong> AccessEVA works most effectively
                          and accurately when using scanned and created PDF and text files. For spreadsheets with “active” formulas and for presentations similar to PowerPoint,
                          AccessEVA still provides appropriate responses to queries but may attempt to query and calculate answers which ability to accurately do so may be imperfect as
                          a current limitation of AI technology. Therefore, AccessEVA recommends that any spreadsheets and presentations be in PDF format to ensure complete and
                          accurate answers to queries.</p>
                      </div>
                    </div>
                    <div className="col-12 helpful-hint mt-2">
                      <div class="card p-3 bg-light">
                        <p className="text-mute mb-0"><strong>Helpful Hint:</strong> Please be aware that the
                          number and sizes of your files to vector into a Project may limit the ability of AccessEVA to query all of the files in a Project. Therefore, for a given Project, please
                          limit:</p>
                        <ul>
                          <li>the number of files to a maximum of 10</li>
                          <li>the size of any file to no more than 5 MB</li>
                          <li>the aggregate size of all files to no more than 20 MB</li>
                        </ul>
                      </div>
                    </div>
                    <div className="col-12 helpful-hint mt-2">
                      <div className="card p-3 bg-light">
                        <p className="text-mute mb-0"><strong>Helpful Hint:</strong> After clicking “Create New
                          Project”, and naming the “New Project”, the “New Project” will appear on the below list. Please then select the “Project” from the below list, and click the “Files
                          Used for Queries and Responses” to either “Attach New” or Attach from Resources” any files to be added to the “Project”.</p>
                      </div>
                    </div>
                    <div class="col-12 helpful-hint mt-2">
                      <div class="card p-3 bg-light">
                        <p class="text-mute mb-0"><strong>Helpful Hint:</strong> When selecting files to “Batch” Vector all of those files at once, select up to 5 files&nbsp;or&nbsp;a maximum of 10 MB for all selected files.</p>
                      </div>
                    </div>
                    <div class="col-12 helpful-hint mt-2">
                      <div class="card p-3 bg-light">
                        <p class="text-mute mb-0"><strong>Helpful Hint:</strong> Embedded/active/live “links” (“Links”) in any selected files will prevent AccessEVA from vectoring the data of those selected files.&nbsp; For any “Project” or the “Resources”, please remove/deactivate all Links from any selected files&nbsp;before&nbsp;clicking “Vector”.&nbsp; One way to remove Links is to print the selected file and then scan those printed pages into a PDF. </p>
                      </div>
                    </div>
                  </>
                )}

              </div>


              <div className="card-header d-flex justify-content-between align-items-center">

                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    className="form-control form-control-sm  me-3"
                    placeholder="Project Name Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '300px' }}
                  />
                  {isEnterpriseUser && (
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" id="filter-dropdown" size="sm">
                        {projectFilter === 'all' ? 'All Projects' :
                          projectFilter === 'owned' ? 'My Projects' : 'Shared With Me'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item active={projectFilter === 'all'} onClick={() => setProjectFilter('all')}>All Projects</Dropdown.Item>
                        <Dropdown.Item active={projectFilter === 'owned'} onClick={() => setProjectFilter('owned')}>My Projects</Dropdown.Item>
                        <Dropdown.Item active={projectFilter === 'shared'} onClick={() => setProjectFilter('shared')}>Shared With Me</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>
                <div>
                  <button type="button" className="btn btn-outline-primary" onClick={() => handleProjectShow()}>Create New Project</button>
                </div>
              </div>

              <div className="table-responsive position-relative">
                <Table className="table table-borderless table-thead-bordered table-nowrap table-align-middle card-table table-striped">
                  <thead className="thead-light border-bottom">
                    <tr>
                      <th style={{ width: '200px' }} class="ps-3">Project Name</th>
                      <th>Creation Date</th>
                      <th>Last Modified Date</th>
                      {/* <th>Status</th> */}
                      <th>Files</th>
                      <th style={{ width: '350px' }}>File Names</th>
                      {isEnterpriseUser && (<th>Access</th>)}
                      <th>Options</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentLists.map((project) => (
                      <tr key={project.id}>
                        <td class="ps-3">
                          <Link to={`/console/chatbot/${project.id}`}>
                            {project.name}
                          </Link>
                        </td>
                        <td>{new Date(project.created_at).toLocaleDateString()}</td>
                        <td>{new Date(project.updated_at).toLocaleDateString()}</td>
                        {/* <td>{project.status}</td> */}
                        <td>{project.files?.length || 0}</td>
                        <td>{project.files?.map(file => file.name).join(', ')}</td>
                        {isEnterpriseUser && (
                          <td>
                            {project.isShared ? (
                              <Badge bg="info">Shared</Badge>
                            ) : (
                              <Badge bg="success">Owner</Badge>
                            )}
                          </td>
                        )}
                        <td>
                          <div className="options-dropdown dropdown">
                            <button className="btn btn-secondary btn-sm dropdown-toggle" type="button" id={`dropdown-${project.id}`} data-bs-toggle="dropdown" aria-expanded="false">
                              {/* <i className="bi bi-three-dots"></i> */}
                              Options
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={`dropdown-${project.id}`}>
                              {!project.isShared && (
                                <>
                                  <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleProjectShow(project); }}><i className="bi bi-pencil me-2"></i>Edit Project</a></li>
                                  <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleDeleteShow(project); }}><i className="bi bi-trash me-2"></i>Delete Project</a></li>
                                  <li><hr className="dropdown-divider" /></li>
                                  <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleUploadShow(project); }}><i className="bi bi-plus-lg me-2"></i>Vector File</a></li>
                                  <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleFileShow(project); }}><i className="bi bi-folder2-open me-2"></i>Select File</a></li>
                                  <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleDelFileShow(project); }}><i className="bi bi-x-lg me-2"></i>Delete File</a></li>
                                </>
                              )}
                              {project.isShared && (
                                <li><a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); handleDetailsShow(project); }}><i className="bi bi-eye me-2"></i>View Details</a></li>
                              )}

                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="card-footer d-flex justify-content-between align-items-center bg-white">
                <div>
                  <Pagination>
                    {pageNumbers.map(number => (
                      <Pagination.Item key={number} active={number === currentPage} onClick={() => paginate(number)}>
                        {number}
                      </Pagination.Item>
                    ))}
                  </Pagination>
                </div>

              </div>
            </div>
          </div>

        </div >
      </div >

      {/* Project Modal */}
      < Modal show={showProjectModal} onHide={handleProjectClose} centered>
        <Form onSubmit={handleProjectSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{currentProject ? 'Edit Project' : 'New Project'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                required
                defaultValue={currentProject?.name || ''}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                rows={3}
                defaultValue={currentProject?.description || ''}
              />
            </Form.Group>
            {/* <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                defaultValue={currentProject?.status || 'Active'}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Form.Select>
            </Form.Group> */}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleProjectClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal >

      {/* File Upload Modal */}
      < Modal show={showUploadModal}
        onHide={handleUploadClose}
        backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Vector File to {currentProject?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UploadFilesTreading
            onUploadComplete={handleUploadComplete}
            onCancel={() => {
              setIsUploading(false);
              handleUploadClose();
            }}
            onUploadingStatusChange={handleUploadingStatus}
            projectId={currentProject?.id}
          />
        </Modal.Body>
      </Modal >

      {/* Attach File Modal */}
      <Modal show={showFileModal} onHide={handleFileClose}
        backdrop="static" centered
      >
        <Modal.Header closeButton>
          <div>
            <h1 className="modal-title fs-5" id="attachFileLabel">Attach from Resources</h1>
            <p className="mb-0">Select and Attach Previously-Vectored Files from Resources</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <AddProjectFiles
            onComplete={handleFileSubmitCompete}
            onCancel={handleFileClose}
            projectId={currentProject?.id}
            projectFileIds={[]}
          />
        </Modal.Body>
      </Modal>


      {/* File delete Modal */}
      < Modal show={showDelFileModal} onHide={handleDelFileClose} centered>
        <Form onSubmit={handleDelFileSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Delete Files to {currentProject?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Select files to delete</Form.Label>
              {currentProject?.files?.map(file => (
                <Form.Check
                  key={file.id}
                  type="checkbox"
                  id={`file-${file.id}`}
                  label={file.name}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedDelFiles([...selectedDelFiles, file.id]);
                    } else {
                      setSelectedDelFiles(selectedDelFiles.filter(id => id !== file.id));
                    }
                  }}
                />
              ))}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDelFileClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Delete File to Project
            </Button>
          </Modal.Footer>
        </Form>
      </Modal >

      {/* Delete Confirmation Modal */}
      < Modal show={showDeleteModal} onHide={handleDeleteClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the project "{currentProject?.name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteProject}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal >

      {/* Project Details Modal */}
      <Modal show={showDetailsModal} onHide={handleDetailsClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Project Details: {currentProject?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentProject && (
            <div>
              <div className="mb-4">
                <h5>Basic Information</h5>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Project Name:</strong> {currentProject.name}</p>
                    <p><strong>Creation Date:</strong> {new Date(currentProject.created_at).toLocaleDateString()}</p>
                    <p><strong>Last Modified:</strong> {new Date(currentProject.updated_at).toLocaleDateString()}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Owner:</strong> {currentProject.isShared ? currentProject.ownerName || "Other User" : "You"}</p>
                    <p><strong>Access Level:</strong> {currentProject.isShared ? (currentProject.permission || "Viewer") : "Owner"}</p>
                    <p><strong>Number of Files:</strong> {currentProject.files?.length || 0}</p>
                  </div>
                </div>
                {currentProject.description && (
                  <div className="mt-2">
                    <h5>Description</h5>
                    <p>{currentProject.description}</p>
                  </div>
                )}
              </div>

              {currentProject.files && currentProject.files.length > 0 && (
                <div>
                  <h5>Files in this Project</h5>
                  <Table className="table table-sm">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Size</th>
                       </tr>
                    </thead>
                    <tbody>
                      {currentProject.files.map(file => (
                        <tr key={file.id}>
                          <td>{file.name}</td>
                          <td>{file.size ? `${Math.round(file.size / 1024)} KB` : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {currentProject.isShared && (
                <div className="mt-4">
                  <h5>Sharing Information</h5>
                  <p><strong>Shared By:</strong> {currentProject.ownerName || "Project Owner"} ({currentProject.ownerEmail})</p>
                  <p><strong>Shared On:</strong> {currentProject.isShared ? new Date(currentProject.shared_at).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>Access Level:</strong> {currentProject.permission || "View Only"}</p>
                  {currentProject.sharingNote && (
                    <div>
                      <p><strong>Note from Owner:</strong></p>
                      <div className="p-2 bg-light rounded">{currentProject.sharingNote}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDetailsClose}>
            Close
          </Button>
          <Link to={`/console/chatbot/${currentProject?.id}`} className="btn btn-primary">
            Open Project
          </Link>
        </Modal.Footer>
      </Modal>

    </>
  );
}

export default Projects;